import {
  acquireExclusiveCustomerDisplayLock,
  CUSTOMER_DISPLAY_LOCK_STATUS,
} from './customerDisplayLock'
import { createCustomerDisplayId } from './customerDisplayId'

const CUSTOMER_DISPLAY_MAIN_APP_PRESENCE_LOCK =
  'customer-display-main-app-presence'
const CUSTOMER_DISPLAY_WINDOW_PRESENCE_LOCK = 'customer-display-window-presence'
const CUSTOMER_DISPLAY_WINDOW_PRESENCE_STORAGE_KEY =
  'customerDisplayWindowPresence'
const CUSTOMER_DISPLAY_WINDOW_PRESENCE_TTL_MS = 5000
const CUSTOMER_DISPLAY_WINDOW_PRESENCE_HEARTBEAT_MS = 1000

const getLockManager = () => globalThis.navigator?.locks
const getPresenceLockName = tabId =>
  tabId
    ? `${CUSTOMER_DISPLAY_MAIN_APP_PRESENCE_LOCK}:${tabId}`
    : CUSTOMER_DISPLAY_MAIN_APP_PRESENCE_LOCK

const getCustomerDisplayPresenceLockNames = async () => {
  const lockManager = getLockManager()
  if (typeof lockManager?.query !== 'function') return null

  try {
    const { held = [] } = await lockManager.query()
    return held.map(lock => lock.name)
  } catch (error) {
    console.warn('No se pudo validar la presencia de la app principal:', error)
    return null
  }
}

const getCustomerDisplayWindowPresence = () => {
  try {
    const value = globalThis.localStorage?.getItem(
      CUSTOMER_DISPLAY_WINDOW_PRESENCE_STORAGE_KEY
    )
    if (!value) return null

    const presence = JSON.parse(value)
    return presence?.instanceId && presence?.updatedAt ? presence : null
  } catch (error) {
    console.warn(
      'No se pudo leer la presencia de la ventana Customer Display:',
      error
    )
    return null
  }
}

const isCustomerDisplayWindowPresenceFresh = presence =>
  Boolean(
    presence?.updatedAt &&
      Date.now() - presence.updatedAt <= CUSTOMER_DISPLAY_WINDOW_PRESENCE_TTL_MS
  )

export const hasCustomerDisplayWindowPresence = () =>
  isCustomerDisplayWindowPresenceFresh(getCustomerDisplayWindowPresence())

export const hasActiveCustomerDisplayWindowPresence = async () => {
  const lockManager = getLockManager()

  if (typeof lockManager?.query === 'function') {
    try {
      const { held = [] } = await lockManager.query()
      return held.some(
        lock => lock.name === CUSTOMER_DISPLAY_WINDOW_PRESENCE_LOCK
      )
    } catch (error) {
      console.warn(
        'No se pudo validar el lock de la ventana Customer Display:',
        error
      )
    }
  }

  return hasCustomerDisplayWindowPresence()
}

const startCustomerDisplayWindowPresence = (
  instanceId,
  onConflict,
  { overwriteExisting = false } = {}
) => {
  let released = false
  let heartbeatInterval = null

  const writePresence = () => {
    if (released) return

    const currentPresence = getCustomerDisplayWindowPresence()
    if (
      !overwriteExisting &&
      currentPresence?.instanceId !== instanceId &&
      isCustomerDisplayWindowPresenceFresh(currentPresence)
    ) {
      released = true
      if (heartbeatInterval) {
        globalThis.clearInterval(heartbeatInterval)
      }
      onConflict?.()
      return
    }

    try {
      globalThis.localStorage?.setItem(
        CUSTOMER_DISPLAY_WINDOW_PRESENCE_STORAGE_KEY,
        JSON.stringify({ instanceId, updatedAt: Date.now() })
      )
    } catch (error) {
      console.warn(
        'No se pudo registrar la presencia de la ventana Customer Display:',
        error
      )
    }
  }

  writePresence()
  if (released) return () => {}

  heartbeatInterval = globalThis.setInterval(
    writePresence,
    CUSTOMER_DISPLAY_WINDOW_PRESENCE_HEARTBEAT_MS
  )

  return () => {
    if (released) return

    released = true
    globalThis.clearInterval(heartbeatInterval)

    if (getCustomerDisplayWindowPresence()?.instanceId === instanceId) {
      globalThis.localStorage?.removeItem(
        CUSTOMER_DISPLAY_WINDOW_PRESENCE_STORAGE_KEY
      )
    }
  }
}

const acquireCustomerDisplayWindowPresenceFallback = onConflict => {
  if (hasCustomerDisplayWindowPresence()) return null

  const instanceId = createCustomerDisplayId()
  return startCustomerDisplayWindowPresence(instanceId, onConflict)
}

export const acquireCustomerDisplayWindowPresence = async onConflict => {
  const lockResult = await acquireExclusiveCustomerDisplayLock(
    CUSTOMER_DISPLAY_WINDOW_PRESENCE_LOCK
  )

  if (
    lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.unsupported ||
    lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.failed
  ) {
    if (lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.failed) {
      console.warn(
        'No se pudo reservar la ventana única de Customer Display:',
        lockResult.error || lockResult.reason
      )
    }
    return acquireCustomerDisplayWindowPresenceFallback(onConflict)
  }

  if (lockResult.status === CUSTOMER_DISPLAY_LOCK_STATUS.unavailable) {
    return null
  }

  const instanceId = createCustomerDisplayId()
  const releasePresence = startCustomerDisplayWindowPresence(
    instanceId,
    onConflict,
    { overwriteExisting: true }
  )

  return () => {
    releasePresence()
    lockResult.release()
  }
}

export const acquireCustomerDisplayMainAppPresence = tabId => {
  const lockManager = getLockManager()
  const lockName = getPresenceLockName(tabId)
  if (typeof lockManager?.request !== 'function') return null

  let releaseLock
  let released = false
  const releasePromise = new Promise(resolve => {
    releaseLock = resolve
  })

  lockManager
    .request(lockName, { mode: 'shared' }, async () => {
      if (released) return
      await releasePromise
    })
    .catch(error => {
      console.warn(
        'No se pudo registrar la presencia de la app principal:',
        error
      )
    })

  return () => {
    if (released) return

    released = true
    releaseLock()
  }
}

export const hasCustomerDisplayMainAppPresence = async (
  tabId,
  heldLockNamesSnapshot
) => {
  const lockName = getPresenceLockName(tabId)
  const heldLockNames =
    heldLockNamesSnapshot ?? (await getCustomerDisplayPresenceLockNames())

  return heldLockNames === null ? null : heldLockNames.includes(lockName)
}

export const getCustomerDisplayMainAppPresenceState = async ownerTabId => {
  const heldLockNames = await getCustomerDisplayPresenceLockNames()
  if (heldLockNames === null) return null

  const ownerLockName = getPresenceLockName(ownerTabId)
  const tabLockPrefix = `${CUSTOMER_DISPLAY_MAIN_APP_PRESENCE_LOCK}:`
  const ownerIsPresent = await hasCustomerDisplayMainAppPresence(
    ownerTabId,
    heldLockNames
  )

  return {
    ownerIsPresent,
    anotherTabIsPresent: heldLockNames.some(
      lockName =>
        lockName.startsWith(tabLockPrefix) && lockName !== ownerLockName
    ),
  }
}
